import { getMallList } from 'app/api/mall';
import { getRedemtionReport, postRedemptionReport } from 'app/api/report';
import { APP_CONSTANTS } from 'app/constants/app-constants';
import { TableHeaderConstant } from 'app/constants/table-header-constants';
import { startExportPoll, triggerDownload } from 'app/utils/exportPoller';
import Message from 'app/utils/Message';
import Toast from 'app/utils/Toast';
import { istMonthEndDate } from 'app/utils/Parsers';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const useRedemptionReport = () => {
  const navigate = useNavigate();
  const [values, setValues] = useState({
    mallId: '',
    selectedMonth: '',
  });
  const [totalNumber, setTotalNumber] = useState(0);
  const [filteredData, setFilteredData] = useState([]);
  const [pageLoading, setPageLoading] = useState(false);
  const [pageCount, setPageCount] = useState(1);
  const currentPage = 1;
  const dataPerPage = 10;
  const [mallList, setMallList] = useState([]);
  const downloadInProgressRef = useRef(false);
  const [downloadInProgress, setDownloadInProgress] = useState(false);

  const sectionObj = {
    sectionTitle: 'Redemption Report',
    downloadObj: {
      excelClick: (e) => {
        handleDownloadexcel(e);
      },
      csvClick: (e) => {
        handleDownloadcsv(e);
      },
      pdfClick: () => () => {},
      inProgress: downloadInProgress,
    },
  };

  const bottomButtons = [
    {
      customClassName: 'cancel_voucher_btn',
      title: APP_CONSTANTS.SAVE,
      handleClick: (e) => {
        const updatedData = filteredData?.filter(
          (item) => item?.voucher_qty || item?.consumption
        );
        if (
          updatedData?.some((item) => !item?.voucher_qty || !item?.consumption)
        ) {
          Toast.error('Please enter all required values.');
          return;
        }
        postReportData(
          values,
          updatedData?.map((item) => ({
            ...item,
            increment_percentage: parseInt(
              item?.increment_percentage?.replace(' %')
            ),
          }))
        );
      },
    },
  ];

  useEffect(() => {
    getMallListforDropdown();
  }, []);

  const getMallListforDropdown = async () => {
    setPageLoading(true);
    let response = await getMallList(1);
    setPageLoading(false);
    if (response && response?.responseCode === 200) {
      let temp = await response?.data?.malls?.map((item, index) => {
        return {
          ...item,
          value: item.id,
          label: item.name,
        };
      });
      temp.unshift({ value: 'All', label: 'All Mall' });
      setMallList([...temp]);
    } else {
      Toast.error(
        response?.data ? response?.data : Message?.Error?.COMMON_MESSAGE
      );
    }
  };

  const postReportData = useCallback(async (filters, payload) => {
    if (!filters.mallId || !filters.selectedMonth) {
      return;
    }
    setPageLoading(true);
    try {
      let requestParams = {
        mallId: filters.mallId,
        month: filters.selectedMonth.split('-')?.[1],
        year: filters.selectedMonth.split('-')?.[0],
      };

      const response = await postRedemptionReport(requestParams, {
        data: payload,
      });
      if (response?.responseCode === 200) {
        Toast.success(response?.response?.message);
      } else {
        Toast.error(
          response?.response?.message || Message.Error.COMMON_MESSAGE
        );
      }
    } catch (e) {
      Toast.error(Message.Error.COMMON_MESSAGE);
    } finally {
      setPageLoading(false);
    }
  }, []);

  const fetchReportData = useCallback(
    async (page, search = '', filters = {}) => {
      if (!filters.mallId || !filters.selectedMonth) {
        return;
      }
      setPageLoading(true);
      try {
        let requestParams = {
          page,
          limit: dataPerPage,
          search,
          mallId: filters.mallId,
          voucherUploadStartDate: filters.selectedMonth
            ? filters.selectedMonth?.toString() + '-01'
            : null,
          voucherUploadEndDate: filters.selectedMonth
            ? istMonthEndDate(filters.selectedMonth)
            : null,
        };

        const response = await getRedemtionReport(requestParams);
        if (response?.responseCode === 200) {
          const dataArr = response?.data?.data || [];
          const newArray = dataArr.map((item) => ({
            ...item,
            brand_denom_uid: item?.brandDenomUid,
          }));
          setFilteredData(newArray);
          const totalCount = response?.data?.count || 0;
          const tempcount = Math.ceil(totalCount / dataPerPage);
          setTotalNumber(totalCount);
          setPageCount(tempcount);
        } else {
          Toast.error(
            response?.response?.message || Message.Error.COMMON_MESSAGE
          );
        }
      } catch (error) {
        Toast.error(Message.Error.COMMON_MESSAGE);
      } finally {
        setPageLoading(false);
      }
    },
    [dataPerPage]
  );

  function handleSubmit(event) {
    event.preventDefault();
    fetchReportData(1, '', values);
  }

  // Async export: returns a jobId, then polls /export/status/:jobId. We DON'T
  // block the UI with pageLoading — the Toast is enough feedback, and forcing
  // a full-page loader prevents the user from working while the export runs
  // (which can take minutes for large datasets).
  async function startDownload(downloadType) {
    if (downloadInProgressRef.current) return;
    downloadInProgressRef.current = true;
    setDownloadInProgress(true);
    let filters = values;
    let requestParams = {
      page: 1,
      limit: totalNumber,
      mallId: filters.mallId,
      voucherUploadStartDate: filters.selectedMonth
        ? filters.selectedMonth?.toString() + '-01'
        : null,
      voucherUploadEndDate: filters.selectedMonth
        ? istMonthEndDate(filters.selectedMonth)
        : null,
      download: true,
      downloadType,
    };

    try {
      const response = await getRedemtionReport(requestParams);
      const jobId = response?.data?.jobId;
      if (response?.responseCode !== 200 || !jobId) {
        Toast.error(response?.response?.message || Message.Error.COMMON_MESSAGE);
        downloadInProgressRef.current = false;
        setDownloadInProgress(false);
        return;
      }

      Toast.success('Report generation started');

      startExportPoll(jobId, {
        onDone: (url) => {
          triggerDownload(url);
          downloadInProgressRef.current = false;
          setDownloadInProgress(false);
        },
        onFailed: (error) => {
          Toast.error(error || Message.Error.COMMON_MESSAGE);
          downloadInProgressRef.current = false;
          setDownloadInProgress(false);
        },
        onError: () => {
          Toast.error(Message.Error.COMMON_MESSAGE);
          downloadInProgressRef.current = false;
          setDownloadInProgress(false);
        },
      });
    } catch (error) {
      console.error('startDownload failed:', error);
      Toast.error(Message.Error.COMMON_MESSAGE);
      downloadInProgressRef.current = false;
      setDownloadInProgress(false);
    }
  }

  async function handleDownloadcsv() {
    return startDownload('csv');
  }

  async function handleDownloadexcel() {
    return startDownload('excel');
  }

  function setChange(e) {
    const { name, value } = e.target;
    setValues((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  }

  function getUpdatedDataItem(item) {
    const val = {
      gv_amt: '',
      increment_amount: '',
      increment_percentage: '',
    };
    if (!isNaN(item?.voucher_qty) && !isNaN(item?.denomination)) {
      val.gv_amt = parseInt(item?.voucher_qty) * parseInt(item?.denomination);
    }
    if (!isNaN(item?.consumption) && !isNaN(val.gv_amt)) {
      val.increment_amount = parseInt(item?.consumption) - parseInt(val.gv_amt);
    }
    if (!isNaN(val.increment_amount) && !isNaN(val.gv_amt)) {
      val.increment_percentage = `${parseInt(
        (parseInt(val.increment_amount) / parseInt(val.gv_amt)) * 100
      )} %`;
    }

    return {
      ...item,
      ...val,
    };
  }

  function calculateDataValues(data) {
    const updatedData = data?.map((item) => {
      return getUpdatedDataItem(item);
    });
    return updatedData;
  }

  const handleInput = (row, type, value) => {
    const updatedData = filteredData?.map((item) => {
      if (
        item?.brandDemomDateUid === row?.brandDemomDateUid &&
        item?.brandDenomUid === row?.brandDenomUid
      ) {
        let updatedItem = {};
        switch (type) {
          case TableHeaderConstant.voucherQtyReceivedByBrandKey:
            updatedItem = {
              ...item,
              voucher_qty: parseInt(value),
            };
            break;
          case TableHeaderConstant.ConsumptionKey:
            updatedItem = {
              ...item,
              consumption: parseInt(value),
            };
            break;
          case TableHeaderConstant.RemarksKey:
            return {
              ...item,
              remarks: value,
            };
          default:
            return { ...item };
        }
        return getUpdatedDataItem(updatedItem);
      } else {
        return { ...item };
      }
    });
    setFilteredData(updatedData);
  };

  return {
    pageLoading,
    filteredData,
    fetchReportData,
    navigate,
    totalNumber,
    currentPage,
    dataPerPage,
    pageCount,
    sectionObj,
    values,
    setValues,
    setChange,
    handleSubmit,
    mallList,
    handleInput,
    bottomButtons,
  };
};

export default useRedemptionReport;
